import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NotificationManager } from 'react-notifications';
import BtChecklistSelect from '../bt-checklist-select/bt-checklist-select';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Badge from '../../components/shared/Badge/Badge';
import { CrossSvg } from '../../components/svgs/cross';
import appState from '../../state/AppStateContainer';
import { EvaluationTagDto, serviceEvaluations } from '../../services/service-evaluations';
import { EvaluationLimits } from '../../util/evaluationContract';
import { TAG_ARCHIVED_LABEL, TAG_CHIP_STYLE } from '../../util/evaluationStatusMeta';
import { reportUnexpected } from '../page-supplier-evaluations/reportUnexpected';
import { errorKeyOf, evaluationErrorMessage } from './evaluationStart';
import TagManager from './TagManager';
import styles from './app-evaluation-start.module.scss';

export type TagPickerProps = {
  value: string[];
  onChange: (ids: string[]) => void;
  tags: EvaluationTagDto[];
  onTagsChange: (tags: EvaluationTagDto[]) => void;
  keepArchivedIds?: string[];
  // True while value is the parent's derived prefill, which already leaves archived tags out.
  prefilled?: boolean;
  tagsLoaded?: boolean;
  label?: string;
};

const MAX_TAGS = EvaluationLimits.MaxTagsPerRequest;

const sameIds = (a: string[], b: string[]) =>
  a.length === b.length && a.every((id, i) => id === b[i]);

const TagPicker = (props: TagPickerProps) => {
  const { value, tags, tagsLoaded = true } = props;
  const [t] = useTranslation('evaluations');
  const [managing, setManaging] = useState(false);
  const latest = useRef(props);
  latest.current = props;
  const emitted = useRef(value);
  // Bumped whenever the parent replaces the picks, so a create that resolves afterwards adds nothing.
  const picksCleared = useRef(0);
  const mounted = useRef(true);

  useEffect(
    () => () => {
      mounted.current = false;
    },
    [],
  );

  useEffect(() => {
    if (!sameIds(value, emitted.current)) picksCleared.current += 1;
    emitted.current = value;
  }, [value]);

  const emit = (next: string[]) => {
    emitted.current = next;
    latest.current.onChange(next);
  };

  const activeOptions = useMemo(
    () => tags.filter(tag => !tag.archived).map(tag => ({ value: tag._id, label: tag.name })),
    [tags],
  );
  const activeIds = new Set(activeOptions.map(option => option.value));
  const archivedTags = value
    .map(id => tags.find(tag => tag._id === id && tag.archived))
    .filter((tag): tag is EvaluationTagDto => Boolean(tag));

  const atCap = value.length >= MAX_TAGS;
  const capText = t(
    'tags.capReached',
    'Högst {{max}} taggar per utvärdering. Ta bort en tagg för att välja en annan.',
    { max: MAX_TAGS },
  );

  const handleChange = (nextActive: string[]) => {
    const kept = value.filter(id => !activeIds.has(id) || nextActive.includes(id));
    const added = nextActive.filter(id => !value.includes(id));
    const room = Math.max(0, MAX_TAGS - kept.length);
    if (added.length > room) NotificationManager.info(capText);
    emit([...kept, ...added.slice(0, room)]);
  };

  const handleCreate = async (name: string) => {
    const clearedAtStart = picksCleared.current;
    try {
      const created = await serviceEvaluations.addOrFindTag(name);
      if (!mounted.current) return;
      const current = latest.current;
      if (!current.tags.some(tag => tag._id === created._id)) {
        current.onTagsChange([...current.tags, created]);
      }
      if (picksCleared.current !== clearedAtStart) return;
      if (current.value.includes(created._id) || current.value.length >= MAX_TAGS) return;
      emit([...current.value, created._id]);
    } catch (error) {
      reportUnexpected(error);
      const message = evaluationErrorMessage(t, error);
      if (!message) return;
      NotificationManager.error(
        errorKeyOf(error) ? message : t('tags.createFailed', 'Det gick inte att skapa taggen'),
      );
    }
  };

  return (
    <div className={styles.tagPicker}>
      <BtChecklistSelect
        label={props.label ?? t('tags.label', 'Taggar')}
        placeholder={t('tags.placeholder', 'Välj taggar')}
        options={activeOptions}
        value={value.filter(id => activeIds.has(id))}
        onChange={handleChange}
        onCreate={appState.isWriteAccessRole() && !atCap ? handleCreate : undefined}
        searchPlaceholder={
          atCap
            ? t('tags.capSearch', 'Högst {{max}} taggar. Ta bort en för att välja en annan.', {
                max: MAX_TAGS,
              })
            : undefined
        }
      />
      {archivedTags.length > 0 && (
        <div className={styles.tagRow}>
          {archivedTags.map(tag => (
            <Badge
              key={tag._id}
              color={TAG_CHIP_STYLE.color}
              backgroundColor={TAG_CHIP_STYLE.backgroundColor}
              className={`${styles.tagChip} ${styles.archivedChip}`}
            >
              {tag.name}
              <span className={styles.archivedNote}>
                {` · ${t(TAG_ARCHIVED_LABEL.labelKey, TAG_ARCHIVED_LABEL.fallback)}`}
              </span>
              <button
                type="button"
                className={styles.chipRemove}
                aria-label={t('tags.remove', 'Ta bort {{name}}', { name: tag.name })}
                onClick={() => emit(latest.current.value.filter(id => id !== tag._id))}
              >
                <CrossSvg size="8px" color="var(--gray-700)" />
              </button>
            </Badge>
          ))}
        </div>
      )}
      {atCap && <p className={styles.hint}>{capText}</p>}
      {tagsLoaded && appState.isAdminRole() && (
        <div className={styles.manageToggle}>
          <button
            type="button"
            className={styles.quietAction}
            aria-expanded={managing}
            onClick={() => setManaging(open => !open)}
          >
            {t('tags.manage', 'Hantera taggar')}
            {managing ? (
              <ExpandLessIcon style={{ fontSize: 16 }} aria-hidden />
            ) : (
              <ExpandMoreIcon style={{ fontSize: 16 }} aria-hidden />
            )}
          </button>
        </div>
      )}
      {managing && tagsLoaded && appState.isAdminRole() && (
        <TagManager
          tags={tags}
          onTagsChange={next => latest.current.onTagsChange(next)}
          onArchived={id => {
            const current = latest.current;
            if (current.prefilled || (current.keepArchivedIds ?? []).includes(id)) return;
            if (current.value.includes(id)) emit(current.value.filter(kept => kept !== id));
          }}
        />
      )}
    </div>
  );
};

export default TagPicker;
